import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth, API } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  MessageCircle, Send, Loader2, User, Clock, Check, 
  CheckCheck, ArrowLeft, RefreshCcw, X
} from 'lucide-react';

const AdminChat = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const pollInterval = useRef(null);
  const prevMessageCount = useRef(0);

  useEffect(() => {
    fetchSessions();
    
    // Poll for new sessions
    const interval = setInterval(fetchSessions, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedSession) {
      setShouldAutoScroll(true); // Reset auto-scroll when selecting new session
      fetchMessages(selectedSession.session_id);
      
      // Poll for new messages
      pollInterval.current = setInterval(() => {
        fetchMessages(selectedSession.session_id);
      }, 3000);
    }
    
    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, [selectedSession]);

  // Only auto-scroll when new messages arrive and user hasn't scrolled up
  useEffect(() => {
    if (messages.length > prevMessageCount.current && shouldAutoScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevMessageCount.current = messages.length;
  }, [messages, shouldAutoScroll]);

  // Detect if user has scrolled up
  const handleScroll = (e) => {
    const container = e.target;
    const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 50;
    setShouldAutoScroll(isAtBottom);
  };

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/admin/chat/sessions`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    }
    setLoading(false);
  };

  const fetchMessages = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/chat/messages/${sessionId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setMessages(response.data.messages || []);
      
      // Refresh sessions to update unread counts after marking messages as read
      fetchSessions();
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
    setLoadingMessages(false);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedSession) return;
    
    setSending(true);
    try {
      await axios.post(`${API}/admin/chat/messages`, {
        session_id: selectedSession.session_id,
        message: newMessage.trim()
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      setMessages(prev => [...prev, {
        message_id: `temp_${Date.now()}`,
        sender: 'admin',
        sender_name: user?.name || 'Support',
        message: newMessage.trim(),
        timestamp: new Date().toISOString()
      }]);
      
      setNewMessage('');
      fetchSessions(); // Refresh to update unread counts
    } catch (error) {
      toast.error('Failed to send message');
    }
    setSending(false);
  };

  const updateSessionStatus = async (sessionId, status) => {
    try {
      await axios.put(`${API}/admin/chat/sessions/${sessionId}/status`, 
        { status },
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }}
      );
      
      toast.success(`Chat marked as ${status}`);
      fetchSessions();
      
      if (selectedSession?.session_id === sessionId) {
        setSelectedSession(prev => ({ ...prev, status }));
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500/10 text-green-400 border-green-500/20';
      case 'resolved': return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case 'closed': return 'bg-white/10 text-white/50 border-white/20';
      default: return 'bg-white/10 text-white/70 border-white/20';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#CCFF00] animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[#050505]" data-testid="admin-chat">
      <Navbar />
      
      <main className="flex-1 pt-32 pb-4 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center gap-4 mb-4 flex-shrink-0">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="text-white/50 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <span className="overline text-[#CCFF00]">Customer Support</span>
              <h1 className="text-2xl font-['Chivo'] font-black text-white">
                Live Chat
              </h1>
            </div>
            <Button
              variant="outline"
              onClick={fetchSessions}
              className="ml-auto border-white/20 text-white hover:bg-white/5"
            >
              <RefreshCcw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1 min-h-0">
            {/* Sessions List */}
            <div className="lg:col-span-1 flex flex-col min-h-0">
              <div className="bg-[#111] border border-white/10 rounded-sm flex flex-col h-full">
                <div className="p-4 border-b border-white/10 flex-shrink-0">
                  <h2 className="text-white font-bold">Conversations</h2>
                  <p className="text-white/50 text-sm">{sessions.length} total</p>
                </div>
                
                <div className="divide-y divide-white/10 flex-1 overflow-y-auto">
                  {sessions.length === 0 ? (
                    <div className="p-8 text-center">
                      <MessageCircle className="w-10 h-10 text-white/20 mx-auto mb-3" />
                      <p className="text-white/50">No conversations yet</p>
                    </div>
                  ) : (
                    sessions.map((session) => (
                      <div
                        key={session.session_id}
                        onClick={() => {
                          setSelectedSession(session);
                          setLoadingMessages(true);
                        }}
                        className={`p-4 cursor-pointer hover:bg-white/5 transition-colors ${
                          selectedSession?.session_id === session.session_id ? 'bg-white/10' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#CCFF00]/10 rounded-full flex items-center justify-center">
                              <User className="w-5 h-5 text-[#CCFF00]" />
                            </div>
                            <div>
                              <p className="text-white font-medium">{session.customer_name || 'Guest'}</p>
                              <p className="text-white/50 text-xs">{session.customer_email || 'No email'}</p>
                            </div>
                          </div>
                          {session.unread_count > 0 && (
                            <div className="bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                              {session.unread_count}
                            </div>
                          )}
                        </div>
                        
                        {session.last_message && (
                          <p className="text-white/40 text-sm mt-2 truncate">
                            {session.last_message.sender === 'admin' ? 'You: ' : ''}
                            {session.last_message.message}
                          </p>
                        )}
                        
                        <div className="flex items-center justify-between mt-2">
                          <Badge variant="outline" className={`text-xs capitalize ${getStatusColor(session.status)}`}>
                            {session.status}
                          </Badge>
                          <span className="text-white/30 text-xs">
                            {new Date(session.updated_at).toLocaleDateString('en-AU')}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Chat Area */}
            <div className="lg:col-span-2">
              <div className="bg-[#111] border border-white/10 rounded-sm h-full flex flex-col">
                {selectedSession ? (
                  <>
                    {/* Chat Header */}
                    <div className="p-4 border-b border-white/10 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#CCFF00]/10 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-[#CCFF00]" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{selectedSession.customer_name || 'Guest'}</p>
                          <p className="text-white/50 text-xs">{selectedSession.customer_email || 'No email'}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {/* Show different buttons based on current status */}
                        {selectedSession.status === 'active' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateSessionStatus(selectedSession.session_id, 'resolved')}
                              className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Resolve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateSessionStatus(selectedSession.session_id, 'closed')}
                              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                            >
                              <X className="w-4 h-4 mr-1" />
                              Close
                            </Button>
                          </>
                        )}
                        {selectedSession.status === 'resolved' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateSessionStatus(selectedSession.session_id, 'active')}
                              className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                            >
                              <RefreshCcw className="w-4 h-4 mr-1" />
                              Reopen
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateSessionStatus(selectedSession.session_id, 'closed')}
                              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                            >
                              <X className="w-4 h-4 mr-1" />
                              Close
                            </Button>
                          </>
                        )}
                        {selectedSession.status === 'closed' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateSessionStatus(selectedSession.session_id, 'active')}
                            className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                          >
                            <RefreshCcw className="w-4 h-4 mr-1" />
                            Reopen
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Messages */}
                    <div 
                      ref={messagesContainerRef}
                      onScroll={handleScroll}
                      className="flex-1 overflow-y-auto p-4 space-y-3"
                    >
                      {loadingMessages ? (
                        <div className="flex items-center justify-center h-full">
                          <Loader2 className="w-6 h-6 text-[#CCFF00] animate-spin" />
                        </div>
                      ) : messages.length === 0 ? (
                        <div className="flex items-center justify-center h-full">
                          <p className="text-white/50">No messages yet</p>
                        </div>
                      ) : (
                        messages.map((msg) => (
                          <div
                            key={msg.message_id}
                            className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[70%] rounded-lg px-4 py-2 ${
                                msg.sender === 'admin'
                                  ? 'bg-[#CCFF00] text-black'
                                  : 'bg-white/10 text-white'
                              }`}
                            >
                              <p className={`text-xs mb-1 ${
                                msg.sender === 'admin' ? 'text-black/50' : 'text-white/50'
                              }`}>
                                {msg.sender_name}
                              </p>
                              <p className="whitespace-pre-wrap">{msg.message}</p>
                              <div className={`flex items-center gap-1 mt-1 text-xs ${
                                msg.sender === 'admin' ? 'text-black/50' : 'text-white/30'
                              }`}>
                                <Clock className="w-3 h-3" />
                                {new Date(msg.timestamp).toLocaleTimeString('en-AU', { 
                                  hour: '2-digit', 
                                  minute: '2-digit' 
                                })}
                                {msg.sender === 'admin' && (
                                  msg.read ? (
                                    <CheckCheck className="w-3 h-3 ml-1" />
                                  ) : (
                                    <Check className="w-3 h-3 ml-1" />
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Input - Show for active and resolved, hide only for closed */}
                    {selectedSession.status !== 'closed' ? (
                      <div className="p-4 border-t border-white/10">
                        <div className="flex gap-2">
                          <Input
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Type your reply..."
                            className="bg-white/5 border-white/10 text-white flex-1"
                            disabled={sending}
                          />
                          <Button
                            onClick={sendMessage}
                            disabled={sending || !newMessage.trim()}
                            className="bg-[#CCFF00] text-black hover:bg-[#B3E600] px-4"
                          >
                            {sending ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 border-t border-white/10 text-center">
                        <p className="text-white/40 text-sm">This conversation is closed. Reopen to send messages.</p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <MessageCircle className="w-16 h-16 text-white/10 mx-auto mb-4" />
                      <p className="text-white/50">Select a conversation to start</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminChat;
